import React, { useEffect, useState } from "react";
import { use } from "react";
import { Layer, Rect, Circle, Transformer, Text, Line } from "react-konva";
import checkForOverlap from "./overlapCheck.component";
import { sortPointsClockwise } from "./point-sorting.component";

export default function Mask(props) {
    const trRef = React.useRef();
    const shapeRef = React.useRef();
    const [overlapInfo, setOverlapInfo] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const shouldFlipY = Boolean(props.flipY && Number.isFinite(props.worldHeight));

    useEffect(() => {
        if (trRef.current && props.selected && props.maskType !== "polygon") {
            trRef.current.nodes([shapeRef.current]);
            trRef.current.getLayer().batchDraw();
        }
    }, [props.selected]);

    const getDynamicFontSize = () => {
        const safeScale = Math.max(0.1, Number(props.stageScale) || 1);
        return Math.max(0.22, 2.6 / safeScale);
    };
    const textFlipProps = props.textUnflipY
        ? { scaleY: -1, offsetY: getDynamicFontSize() }
        : {};

    const getTextPosition = () => {
        const fontSize = getDynamicFontSize();
        const inset = Math.max(0.2, fontSize * 0.2);

        if (!shapeRef.current) return { x: 0, y: 0 };

        const node = shapeRef.current;
        const nodeX = node.x();
        const nodeY = node.y();
        const scaleX = node.scaleX();
        const scaleY = node.scaleY();
        const radius = props.mask.radius;

        if (props.maskType === "rectangle") {
            const visualTopY = shouldFlipY ? (nodeY + node.height()) : nodeY;
            return {
                x: nodeX + inset,
                y: props.textUnflipY ? (visualTopY + inset) : (visualTopY - fontSize - inset),
            };
        } else if (props.maskType === "circle") {
            const visualTopY = shouldFlipY
                ? (nodeY + radius * scaleY)
                : (nodeY - radius * scaleY);
            return {
                x: nodeX - radius * scaleX + inset,
                y: props.textUnflipY ? (visualTopY + inset) : (visualTopY - fontSize - inset),
            };
        }
        return { x: 0, y: 0 };
    };

    const handleDragEnd = (e) => {
        props.updateMask(e, props.i, props.maskType);
        setIsDragging(false);
    };

    const handleTransformEnd = (e) => {
        props.updateMask(e, props.i, props.maskType);
    };

    useEffect(() => {
        const overlapInfo = checkForOverlap(props.maskList);
        setOverlapInfo(overlapInfo);
    }, [props.maskList]);

    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    // Rendering
    // ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

    return (
        (props.showAllMasks) && (
            <Layer key={props.i}>
                {props.mask.name && !props.mask.visible && (
                    <Text
                        text={props.mask.name}
                        x={getTextPosition().x}
                        y={getTextPosition().y}
                        align="left"
                        fontSize={getDynamicFontSize()}
                        fontStyle="bold"
                        fill="black"
                        {...textFlipProps}
                    />
                )}

                {props.maskType === "rectangle" ? (
                    <Rect
                        ref={shapeRef}
                        x={props.mask.x * props.dxfScale}
                        y={(shouldFlipY
                            ? (props.worldHeight - props.mask.y)
                            : props.mask.y) * props.dxfScale}
                        scaleX={props.mask.scaleX}
                        scaleY={props.mask.scaleY}
                        rotation={props.mask.rotation}
                        width={props.mask.width * props.dxfScale}
                        height={props.mask.height * props.dxfScale}
                        fill={props.mask.color || "lightblue"}
                        opacity={props.mask.opacity}
                        draggable={props.mask.selected}
                        listening={!props.mask.locked}
                        onTap={() => props.selectMask(props.mask.id)}
                        onClick={() => props.selectMask(props.mask.id)}
                        onDragEnd={handleDragEnd}
                        onDragStart={() => setIsDragging(true)}
                    />
                ) : props.maskType === "circle" ? (
                    <Circle
                        ref={shapeRef}
                        x={props.mask.x * props.dxfScale}
                        y={(shouldFlipY ? (props.worldHeight - props.mask.y) : props.mask.y) * props.dxfScale}
                        scaleX={props.mask.scaleX}
                        scaleY={props.mask.scaleY}
                        radius={props.mask.radius * props.dxfScale}
                        fill={props.mask.color || "lightblue"}
                        draggable={props.mask.selected}
                        listening={!props.mask.locked}
                        opacity={props.mask.opacity}
                        onTap={() => props.selectMask(props.mask.id)}
                        onClick={() => props.selectMask(props.mask.id)}
                        onDragEnd={handleDragEnd}
                        onDragStart={() => setIsDragging(true)}
                    />
                ) : null}

                {!isDragging &&
                    overlapInfo
                        .filter(
                            (overlap) =>
                                overlap.mask1Id === props.mask.id || overlap.mask2Id === props.mask.id
                        )
                        .map((overlap, index) => {
                            const sortedPoints = sortPointsClockwise(overlap, props.maskList);
                            const displayPoints = shouldFlipY
                                ? sortedPoints.map(p => ({ x: p.x, y: props.worldHeight - p.y }))
                                : sortedPoints;
                            return sortedPoints.length > 2 ? (
                                <Line
                                    key={index}
                                    points={displayPoints.flatMap(p => [p.x, p.y])}
                                    fill="black"
                                    closed
                                    listening={false}
                                    globalCompositeOperation="destination-out"
                                />
                            ) : null;
                        })}


                {props.selected && (
                    <Transformer
                        ref={trRef}
                        keepRatio={true}
                        anchorSize={Math.max(4, 10 / Math.max(0.5, props.stageScale || 1))}
                        borderStrokeWidth={Math.max(0.5, 1 / Math.max(0.5, props.stageScale || 1))}
                        onTransformEnd={handleTransformEnd}
                    />
                )}
            </Layer>
        )
    );
}
